<template>
    <div class="module">
        <header class="module__header">
            <a
                class="back-btn"
                @click="goBack('radios')"
            >
                <span class="icon-chevron-left" />
            </a>
            <h1 class="has-back-btn">Bulk Upload Radios</h1>
        </header>

        <section class="module__actions">
            <button
                v-if="validated"
                :class="{ active: submitted }"
                class="btn btn-primary"
                data-test-id="import-radios-button"
                @click="importRadios()"
            >
                <div>Import {{ radios.length }} Radios</div>
            </button>
            <a
                v-else
                :href="downloadUrl(templateFilename)"
                class="btn"
                data-test-id="download-template-button"
                download
            >
                <span class="icon-export"></span> Download Import Template
            </a>
        </section>

        <section class="module__body">
            <form
                v-if="!validated"
                @submit.prevent="upload"
            >
                <div class="form-group">
                    <file-upload
                        :url="`${organizationSlug}/assets/radios`"
                        accept=".csv"
                        @error="(formErrorResponse) => (errorResponse = formErrorResponse)"
                        @success="tempUploadComplete"
                    >
                    </file-upload>
                </div>

                <form-errors :response="errorResponse"></form-errors>

                <div class="form-group">
                    <button
                        :class="{ active: submitted }"
                        :disabled="!tempFileUploaded"
                        class="btn btn-primary"
                        data-test-id="upload-button"
                        type="submit"
                    >
                        <div>Upload</div>
                    </button>
                </div>
            </form>

            <p
                v-if="rowErrorLength"
                class="bulk-import-error-notification"
                data-test-id="error-summary"
            >
                {{ rowErrorLength }} of {{ radios.length }} rows had errors. Please review them below and re-upload your
                CSV file.
            </p>

            <table
                v-if="radios.length"
                class="index index-view index-import"
                data-test-id="import-preview-table"
            >
                <thead>
                    <tr>
                        <th scope="col">Row</th>
                        <th scope="col">Type</th>
                        <th scope="col">Serial #</th>
                        <th scope="col">Customer Asset #</th>
                        <th scope="col">Description</th>
                    </tr>
                </thead>
                <tbody>
                    <!-- eslint-disable vue/no-v-for-template-key -->
                    <template
                        v-for="(radio, index) in radios"
                        :key="`d${index}`"
                    >
                        <!-- eslint-enable -->
                        <tr
                            :class="{ odd: index % 2 !== 0, 'row-error': index in rowErrors }"
                            :data-test-id="`row-${index}`"
                        >
                            <td :data-test-id="`row-${index}-col-row`">{{ index + 1 }}</td>
                            <td :data-test-id="`row-${index}-col-type`">
                                {{ radio['type']
                                }}<span
                                    v-if="rowHasError('type', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-serial_number`">
                                {{ radio['serial_number']
                                }}<span
                                    v-if="rowHasError('serial_number', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-asset_number`">
                                {{ radio['asset_number']
                                }}<span
                                    v-if="rowHasError('asset_number', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td :data-test-id="`row-${index}-col-description`">
                                {{ radio['description']
                                }}<span
                                    v-if="rowHasError('description', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                        </tr>

                        <tr
                            v-if="rowErrorLength && rowErrors[index]"
                            :key="`e${index}`"
                            :class="{ odd: index % 2 !== 0 }"
                            data-test-id="import-errors-row"
                        >
                            <td colspan="5">
                                <ul class="bulk-import-errors">
                                    <li
                                        v-for="(error, errorIndex) in formattedRowErrors[index]"
                                        :key="errorIndex"
                                    >
                                        {{ error }}
                                    </li>
                                </ul>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </section>
    </div>
</template>

<script>
import * as _ from 'lodash'
import FileUpload from '@/components/FileUpload/index.vue'
import FormErrors from '@/components/FormErrors.vue'
import canToast from '@/mixins/canToast'
import hasBackButton from '@/mixins/hasBackButton'
import hasDownloadableFile from '@/mixins/hasDownloadableFile'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'

export default {
    components: {
        FileUpload,
        FormErrors,
    },

    mixins: [canToast, hasBackButton, hasDownloadableFile, needsOrganizationSlug],

    data() {
        return {
            filenames: {},
            validated: false,
            radios: [],
            errorResponse: null,
            rowErrors: {},
            submitted: false,
            tempFileUploaded: false,
        }
    },

    computed: {
        formattedRowErrors() {
            const formatted = {}

            _.forEach(this.rowErrors, (rowError, index) => {
                let errorList = []

                _.forEach(rowError, (errors, property) => {
                    errorList = errorList.concat(errors)
                })

                formatted[index] = errorList
            })

            return formatted
        },

        rowErrorLength() {
            return Object.keys(this.rowErrors).length
        },

        templateFilename() {
            return 'portal-bulk-asset-radio-upload-template.csv'
        },
    },

    methods: {
        tempUploadComplete(filenames) {
            this.filenames = filenames
            this.errorResponse = null
            this.tempFileUploaded = true
        },

        upload() {
            if (!this.tempFileUploaded || this.submitted) {
                return
            }
            this.submitted = true
            this.$http
                .post(`${this.organizationSlug}/assets/radios/validate-upload`, {
                    temp_filename: this.filenames.temp,
                    original_filename: this.filenames.original,
                    organization_id: this.organizationId,
                })
                .then(({ data }) => {
                    this.validated = true
                    this.submitted = false
                    this.radios = data
                    this.errorResponse = null
                    this.title = 'Confirm Radios Import'
                    this.toast('Radios ready to import!')
                })
                .catch((error) => {
                    const { type } = error.response.data
                    if (type === 'row-error') {
                        // handle errors in row data
                        const response = error.response.data
                        this.errorResponse = null
                        this.rowErrors = response.errors
                        this.radios = response.radios
                    } else {
                        // handle generic error
                        this.errorResponse = error.response
                        this.rowErrors = {}
                        this.radios = []
                    }
                    this.submitted = false
                })
        },

        importRadios() {
            if (this.submitted) {
                return
            }

            this.submitted = true
            this.$http
                .post(`${this.organizationSlug}/assets/radios/import-upload`, {
                    temp_filename: this.filenames.temp,
                    radio_count: this.radios.length,
                    organization_id: this.organizationId,
                })
                .then(({ data }) => {
                    this.toast('Radios successfully imported!')
                    this.$router.push(`/${this.organizationSlug}/relays/radios`)
                })
                .catch((error) => {
                    this.toast('There was a problem importing the radios.', 'error')
                    this.submitted = false
                    this.errorResponse = { data: { errors: ['There was a problem importing the radios.'] } }
                })
        },

        rowHasError(type, index) {
            return index in this.rowErrors && type in this.rowErrors[index]
        },
    },
}
</script>

<style scoped>
.bulk-import-error-notification {
    margin: 45px 0 30px 0;
}

.bulk-import-errors {
    font-family: 'Avenir Heavy';
    font-size: 0.9125em;
}

.bulk-import-errors li {
    margin-bottom: 12px;
}

.bulk-import-errors,
.has-error {
    color: #f96a62;
}

table.index tbody tr:nth-child(even) {
    background-color: #434a4d;
}

.index-import tbody tr {
    background-color: #434a4d;
}

.index-import tbody tr.row-error {
    border-bottom: 0;
}

.index-import tbody tr.odd {
    background-color: #484f52;
}

.module__body {
    overflow-x: scroll;
}
</style>
